import styled, { createGlobalStyle } from 'styled-components';
import WithDirection from '@iso/lib/helpers/rtl';

/**
 * Training-only global style overrides.
 * Mounted inside <Training/>; unmounted when user leaves the page,
 * so these rules never affect Dashboard, Health & Safety, etc.
 *
 * Add Training-specific layout tweaks here (e.g. <main> padding/bg overrides).
 */
export const TrainingGlobalStyle = createGlobalStyle`
  /* Example: override the app-wide <main class="ant-layout-content"> styling
     (defined in src/Layout/PrivateLayout/PrivateLayout.js) but ONLY while
     the Training page is rendered. Tweak / extend as needed. */
  main.ant-layout-content.isomorphicContent {
    padding: 90px 0 0 !important;  /* keep room for fixed header; drop side padding so our wrapper owns it */
    background: transparent !important;
  }

  main.ant-layout-content.isomorphicContent > div > .isoBoxWrapper{
    background: transparent !important;
    border-top: 0 !important;
  }
`;

const TrainingWrapper = styled.div`
  width: 100%;
  min-height: 100%;
  padding: 16px 24px;

  /* Reusable card primitive */
  .section-card {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    padding: 20px;
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #0B2A1B;
    margin: 0 0 12px 0;
  }

  .training-header {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    padding: 16px 20px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .training-header h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #0B2A1B;
  }

  .btn-primary {
    background-color: #0B2A1B !important;
    border-color: #0B2A1B !important;
    height: 38px;
    border-radius: 8px;
    font-weight: 500;
    &:hover {
      opacity: 0.85;
    }
  }

  .btn-outline {
    height: 38px;
    border-radius: 8px;
    border-color: #0B2A1B;
    color: #0B2A1B;
    font-weight: 500;
    &:hover {
      background-color: #0B2A1B;
      color: #fff;
    }
  }

  .search-bar {
    background: rgba(143, 146, 161, 0.05);
    border-radius: 8px;
    height: 38px;
    min-width: 280px;
  }

  .filter-select {
    min-width: 160px;
    .ant-select-selector {
      border-radius: 8px !important;
      height: 38px !important;
      align-items: center;
    }
  }

  .filter-row {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
  }

  /* Tabs strip rendered as its own card */
  & > .ant-tabs > .ant-tabs-nav {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    padding: 0 16px;
    margin-bottom: 16px;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #0B2A1B !important;
    font-weight: 600;
  }

  .ant-tabs-ink-bar {
    background: #0B2A1B !important;
  }

  .ant-tabs-tab:hover {
    color: #0B2A1B;
  }

  .tab-content {
    padding: 0;
  }

  /* Stat cards */
  .stat-card {
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    transition: box-shadow 0.2s;
    &:hover {
      box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    }
    .ant-statistic-title {
      font-size: 13px;
      color: #8c8c8c;
      font-weight: 500;
    }
    .ant-statistic-content-value {
      font-size: 28px;
      font-weight: 700;
      color: #0B2A1B;
    }
  }

  .stat-card-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 12px;
  }

  /* Compliance snapshot inner content (card chrome comes from .section-card) */
  .compliance-snapshot {
    .snapshot-title {
      font-size: 15px;
      font-weight: 600;
      color: #0B2A1B;
      margin-bottom: 16px;
    }

    .snapshot-item {
      text-align: center;
      padding: 12px;
      border-radius: 8px;
      .count {
        font-size: 24px;
        font-weight: 700;
        display: block;
      }
      .label {
        font-size: 12px;
        color: #8c8c8c;
        font-weight: 500;
      }
    }

    .snapshot-compliant { background: #f6ffed; .count { color: #52c41a; } }
    .snapshot-expiring  { background: #fffbe6; .count { color: #faad14; } }
    .snapshot-expired   { background: #fff1f0; .count { color: #f5222d; } }
    .snapshot-missing   { background: #f9f0ff; .count { color: #722ed1; } }
  }

  /* Quick actions inner content (card chrome comes from .section-card) */
  .quick-actions {
    .quick-action-title {
      font-size: 15px;
      font-weight: 600;
      color: #0B2A1B;
      margin-bottom: 12px;
    }

    .quick-action-btn {
      width: 100%;
      text-align: left;
      height: 42px;
      border-radius: 8px;
      margin-bottom: 8px;
      border-color: #e8e8e8;
      &:hover {
        border-color: #0B2A1B;
        color: #0B2A1B;
      }
    }
  }

  /* Status tags */
  .status-compliant  { background: #f6ffed; color: #52c41a; border-color: #b7eb8f; }
  .status-expiring   { background: #fffbe6; color: #d4a017; border-color: #ffe58f; }
  .status-expired    { background: #fff1f0; color: #f5222d; border-color: #ffa39e; }
  .status-missing    { background: #f9f0ff; color: #722ed1; border-color: #d3adf7; }

  /* Asset grid */
  .asset-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }

  .asset-card {
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    background: #fff;
    padding: 16px;
    cursor: pointer;
    transition: box-shadow 0.2s, border-color 0.2s;
    &:hover {
      box-shadow: 0 4px 16px rgba(0,0,0,0.10);
      border-color: #0B2A1B;
    }

    .asset-name {
      font-size: 15px;
      font-weight: 600;
      color: #0B2A1B;
      margin-bottom: 4px;
    }

    .asset-desc {
      font-size: 13px;
      color: #8c8c8c;
      margin-bottom: 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .asset-meta {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 10px;
    }

    .asset-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid #f0f0f0;
      padding-top: 10px;
      font-size: 12px;
      color: #8c8c8c;
    }
  }

  .contractor-badge {
    background: #e6f7ff;
    color: #1890ff;
    border-color: #91d5ff;
    font-size: 11px;
  }

  /* Table */
  .ant-table-thead > tr > th {
    background: #fafafa;
    font-weight: 600;
    color: #0B2A1B;
    border-bottom: 2px solid #e8e8e8;
  }

  .ant-table-row:hover td {
    background: #f6fff9 !important;
  }

  .ant-pagination-item-active {
    border-color: #0B2A1B;
    a { color: #0B2A1B; }
  }

  .ant-pagination-item:hover {
    border-color: #0B2A1B;
    a { color: #0B2A1B; }
  }

  /* Days until expiry coloring */
  .days-expired   { color: #f5222d; font-weight: 600; }
  .days-critical  { color: #fa541c; font-weight: 600; }
  .days-warning   { color: #faad14; font-weight: 500; }
  .days-ok        { color: #52c41a; }

  /* Wizard steps */
  .ant-steps-item-process .ant-steps-item-icon {
    background: #0B2A1B;
    border-color: #0B2A1B;
  }

  .ant-steps-item-finish .ant-steps-item-icon {
    border-color: #0B2A1B;
    .ant-steps-icon { color: #0B2A1B; }
  }

  .wizard-section {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    border: 1px solid #e8e8e8;
    margin-top: 20px;
  }

  /* Recent activity */
  .activity-item {
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 13px;

    &:last-child { border-bottom: none; }

    .activity-desc { color: #262626; }
    .activity-meta { color: #8c8c8c; font-size: 12px; margin-top: 2px; }
  }

  /* People search */
  .person-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    margin-bottom: 8px;
    cursor: pointer;
    transition: border-color 0.2s;
    &:hover { border-color: #0B2A1B; }

    .person-name { font-weight: 600; color: #262626; }
    .person-meta { font-size: 12px; color: #8c8c8c; }
  }

  .person-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #0B2A1B;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 600;
    flex-shrink: 0;
  }

  /* Person Detail */
  .person-detail-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  }

  .person-detail-avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .person-detail-info {
    flex: 1;
  }

  .person-detail-name {
    font-size: 18px;
    font-weight: 700;
    color: #0B2A1B;
    display: flex;
    align-items: center;
  }

  .person-detail-contact {
    display: flex;
    gap: 20px;
    margin-top: 6px;
    font-size: 13px;
    color: #8c8c8c;
    flex-wrap: wrap;
  }

  .person-stat-card {
    background: #fff;
    border-radius: 10px;
    border: 1px solid #e8e8e8;
    padding: 16px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .person-stat-count {
    font-size: 24px;
    font-weight: 700;
    display: block;
  }

  .person-stat-label {
    font-size: 12px;
    color: #8c8c8c;
    font-weight: 500;
  }

  .person-stat-card.snapshot-compliant { background: #f6ffed; .person-stat-count { color: #52c41a; } }
  .person-stat-card.snapshot-expiring  { background: #fffbe6; .person-stat-count { color: #faad14; } }
  .person-stat-card.snapshot-expired   { background: #fff1f0; .person-stat-count { color: #f5222d; } }

  .person-detail-tabs {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    padding: 0 20px 20px;

    .ant-tabs-nav {
      padding: 0;
      margin-bottom: 16px;
    }
  }

  @media only screen and (max-width: 768px) {
    .filter-row { flex-direction: column; align-items: flex-start; }
    .asset-grid { grid-template-columns: 1fr; }
    .search-bar { min-width: 100%; }
    .person-detail-header { flex-direction: column; text-align: center; }
    .person-detail-contact { justify-content: center; }
    .person-detail-name { justify-content: center; }
  }
`;

export default WithDirection(TrainingWrapper);
